import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Trophy, Plus, RefreshCw, Award } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { PageBody } from '@/components/layout/PageBody';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils';
import { FeatureGate } from '@/components/ui/FeatureGate';
import { ComingSoonOverlay } from '@/components/dashboard';

export const Route = createFileRoute('/competitions')({
  component: function CompetitionsPageGated() {
    return (
      <FeatureGate flag="enableCompetitions">
        <CompetitionsPage />
      </FeatureGate>
    );
  },
});

// ---------------------------------------------------------------------------
// Mock data (static prototype — no API calls)
// ---------------------------------------------------------------------------

interface LeaderboardEntry {
  rank: number;
  name: string;
  initials: string;
  calls: number;
  avgScore: number;
}

interface ActiveCompetition {
  title: string;
  endsIn: string;
  leaderboard: LeaderboardEntry[];
}

interface PastCompetition {
  title: string;
  winner: string;
  metric: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RANK_MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

function rankScoreClass(rank: number): string {
  if (rank === 1) return 'text-success';
  if (rank === 2) return 'text-primary';
  if (rank === 3) return 'text-info';
  return 'text-fg-muted';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LeaderboardRow({ entry, t }: { entry: LeaderboardEntry; t: TFunction }) {
  const medal = RANK_MEDALS[entry.rank];
  const isTopThree = entry.rank <= 3;

  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors hover:bg-bg-elevated/50',
        !isTopThree && 'opacity-70'
      )}
    >
      {/* Rank */}
      <span className="w-6 shrink-0 text-center text-sm font-medium select-none">
        {medal ?? <span className="text-fg-subtle tabular-nums">{entry.rank}</span>}
      </span>

      {/* Avatar */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-xs font-semibold text-fg-muted">
        {entry.initials}
      </div>

      {/* Name + stats */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg">{entry.name}</p>
        <p className="text-xs text-fg-muted">
          {t('competitions.callsCount', { count: entry.calls })}
          &nbsp;&middot;&nbsp;
          {t('competitions.avgScore', { score: Math.round(entry.avgScore) })}
        </p>
      </div>

      {/* Score */}
      <span
        className={cn(
          'shrink-0 font-mono text-sm font-semibold tabular-nums',
          rankScoreClass(entry.rank)
        )}
      >
        {entry.avgScore.toFixed(1)}
      </span>
    </div>
  );
}

function ActiveCompetitionPanel({
  competition,
  t,
}: {
  competition: ActiveCompetition;
  t: TFunction;
}) {
  return (
    <section
      aria-label={t('competitions.activeCompetitionAria')}
      className="rounded-lg border border-success bg-bg-surface"
    >
      {/* Panel header */}
      <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="rounded bg-success-dim px-2 py-0.5 text-xs font-medium text-success-fg">
            {t('competitions.active')}
          </span>
          <h2 className="truncate text-sm font-medium text-fg">{competition.title}</h2>
        </div>
        <span className="shrink-0 text-xs text-fg-muted tabular-nums">
          {t('competitions.endsIn')}{' '}
          <span className="font-semibold text-warning">{competition.endsIn}</span>
        </span>
      </div>

      {/* Leaderboard */}
      <div className="px-2 py-2">
        {competition.leaderboard.map((entry) => (
          <LeaderboardRow key={entry.rank} entry={entry} t={t} />
        ))}
      </div>
    </section>
  );
}

function PastCompetitionCard({
  competition,
  t,
}: {
  competition: PastCompetition;
  t: TFunction;
}) {
  return (
    <article className="rounded-lg border border-border bg-bg-surface">
      {/* Card header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
          <h3 className="truncate text-sm font-medium text-fg">{competition.title}</h3>
          <span className="ml-auto shrink-0 inline-flex items-center rounded-full bg-success-dim px-2 py-0.5 text-micro font-medium text-success-fg">
            {t('competitions.completed')}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="px-4 py-3 space-y-1">
        <p className="text-sm text-fg-muted">
          {t('competitions.winnerLabel')}{' '}
          <span className="font-medium text-fg">{competition.winner}</span>
        </p>
        <p className="font-mono text-sm text-fg-subtle">{competition.metric}</p>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function CompetitionsPage() {
  const { t } = useTranslation('dashboard');
  const activeCompetition: ActiveCompetition = {
    title: t('competitions.activeCompetitionTitle'),
    endsIn: t('competitions.activeEndsIn'),
    leaderboard: [
      {
        rank: 1,
        name: t('competitions.names.olenaKovalenko'),
        initials: 'OK',
        calls: 124,
        avgScore: 91.2,
      },
      {
        rank: 2,
        name: t('competitions.names.innaBondarenko'),
        initials: 'IB',
        calls: 112,
        avgScore: 87.4,
      },
      {
        rank: 3,
        name: t('competitions.names.dmitryKozlov'),
        initials: 'DK',
        calls: 76,
        avgScore: 82.1,
      },
      {
        rank: 4,
        name: t('competitions.names.alexPetrov'),
        initials: 'AP',
        calls: 98,
        avgScore: 74.3,
      },
      {
        rank: 5,
        name: t('competitions.names.mariaShevchenko'),
        initials: 'MS',
        calls: 87,
        avgScore: 61.5,
      },
    ],
  };

  const pastCompetitions: PastCompetition[] = [
    {
      title: t('competitions.past.mostCalls.title'),
      winner: t('competitions.names.alexPetrov'),
      metric: t('competitions.past.mostCalls.metric'),
    },
    {
      title: t('competitions.past.fastestResponse.title'),
      winner: t('competitions.names.innaBondarenko'),
      metric: t('competitions.past.fastestResponse.metric'),
    },
  ];

  return (
    <PageBody>
      <PageHeader
        sticky
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
            <BreadcrumbItem current>{t('competitions.breadcrumb')}</BreadcrumbItem>
          </Breadcrumb>
        }
        icon={Award}
        title={t('competitions.title')}
        subtitle={t('competitions.subtitle')}
        actions={
          <>
            <span className="text-xs text-fg-subtle tabular-nums">
              {t('competitions.updated')}
            </span>
            <button
              type="button"
              aria-label={t('competitions.refreshAria')}
              className="rounded p-1.5 text-fg-muted transition-all duration-150 hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="flex h-9 items-center gap-1.5 rounded-button bg-primary px-4 text-sm text-bg transition-all hover:bg-primary-hover hover:shadow-glow-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('competitions.newCompetition')}
            </button>
          </>
        }
      />

      <ComingSoonOverlay>
        <div className="space-y-6">
          {/* Active competition */}
          <ActiveCompetitionPanel competition={activeCompetition} t={t} />

          {/* Past competitions */}
          <section aria-label={t('competitions.pastCompetitionsAria')}>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
              {t('competitions.pastCompetitions')}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {pastCompetitions.map((comp) => (
                <PastCompetitionCard key={comp.title} competition={comp} t={t} />
              ))}
            </div>
          </section>
        </div>
      </ComingSoonOverlay>
    </PageBody>
  );
}
